import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { getApiErrorMessage } from '@moko/utils';
import { FormActions, useFetch, useFormLayoutStyles } from '@moko/ui';
import {
  getContractorAvailabilityRule,
  isValidDaysOfWeek,
  updateContractorAvailabilityRule,
  type ContractorAvailabilityDraft,
  type IContractorAvailabilityRule,
} from '../../services/contractorAvailabilityRuleService';
import { fromApiDateOnly, toApiDateOnly } from '../../utils/dateOnly';
import {
  ContractorAvailabilityForm,
  type ContractorAvailabilityFormValues,
} from './ContractorAvailabilityForm';

const formFields = (rule: {
  startTime: string;
  endTime: string;
  timezoneId: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: boolean;
}) => ({
  startTime: rule.startTime ?? '',
  endTime: rule.endTime ?? '',
  timezoneId: rule.timezoneId,
  effectiveFrom: fromApiDateOnly(rule.effectiveFrom),
  effectiveTo: fromApiDateOnly(rule.effectiveTo),
  status: Boolean(rule.status),
});

const formFromDraft = (
  rule: ContractorAvailabilityDraft
): ContractorAvailabilityFormValues => ({
  ...formFields(rule),
  daysOfWeek: isValidDaysOfWeek(rule.daysOfWeek) ? rule.daysOfWeek : [],
  dayOfWeek: 1,
});

const formFromRule = (
  rule: IContractorAvailabilityRule
): ContractorAvailabilityFormValues => ({
  ...formFields(rule),
  daysOfWeek: [],
  dayOfWeek: rule.dayOfWeek,
});

export type ContractorAvailabilityEditProps = {
  contractorId?: number;
  ruleId: number;
  draftRule?: ContractorAvailabilityDraft;
  submitLabel: string;
  onDraftUpdate?: (data: ContractorAvailabilityDraft) => void;
  onClose: () => void;
  onSaved: () => void;
};

const ContractorAvailabilityEdit = ({
  contractorId,
  ruleId,
  draftRule,
  submitLabel,
  onDraftUpdate,
  onClose,
  onSaved,
}: ContractorAvailabilityEditProps) => {
  const { t } = useTranslation();
  const styles = useFormLayoutStyles();
  const [saveError, setSaveError] = useState<string | null>(null);
  const form = useForm<ContractorAvailabilityFormValues>({
    defaultValues: {
      daysOfWeek: [],
      dayOfWeek: 1,
      startTime: '',
      endTime: '',
      timezoneId: '',
      effectiveFrom: '',
      effectiveTo: '',
      status: true,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset, formState } = form;
  const submitting = formState.isSubmitting;

  const { data, loading, error } = useFetch<IContractorAvailabilityRule>({
    queryKey: [ruleId],
    queryFn: (abortController) =>
      getContractorAvailabilityRule(abortController, ruleId),
    enabled: draftRule == null && contractorId != null,
  });

  useEffect(() => {
    if (draftRule) {
      reset(formFromDraft(draftRule));
      return;
    }
    if (data) reset(formFromRule(data));
  }, [data, draftRule, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!values.effectiveFrom || !values.effectiveTo) return;
    if (contractorId == null && !draftRule) return;
    setSaveError(null);
    const fields = {
      startTime: values.startTime,
      endTime: values.endTime,
      timezoneId: values.timezoneId,
      effectiveFrom: toApiDateOnly(values.effectiveFrom),
      effectiveTo: toApiDateOnly(values.effectiveTo),
      status: values.status,
    };
    try {
      if (draftRule) {
        onDraftUpdate?.({
          id: ruleId,
          ...fields,
          daysOfWeek: values.daysOfWeek,
        });
        return;
      }
      await updateContractorAvailabilityRule(new AbortController(), ruleId, {
        ...fields,
        contractorId: contractorId!,
        dayOfWeek: values.dayOfWeek,
      });
      onSaved();
    } catch (err: unknown) {
      setSaveError(
        getApiErrorMessage(
          err,
          t('failedToSaveRule', 'Failed to save availability rule.')
        )
      );
    }
  });

  if (!draftRule && loading) {
    return <Spinner label={t('loading', 'Loading...')} />;
  }

  if (!draftRule && error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {getApiErrorMessage(
            error,
            t('failedToLoadRule', 'Failed to load availability rule.')
          )}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (!draftRule && !data) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form className={styles.form} onSubmit={onSubmit}>
        {saveError && (
          <MessageBar intent="error">
            <MessageBarBody>{saveError}</MessageBarBody>
          </MessageBar>
        )}
        <ContractorAvailabilityForm
          disabled={submitting}
          multipleDays={draftRule != null}
        />
        <FormActions
          disabled={submitting}
          submitting={submitting}
          submitLabel={submitLabel}
          onClose={onClose}
        />
      </form>
    </FormProvider>
  );
};

export default ContractorAvailabilityEdit;
